import { Clock, Database, Loader2, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useToast } from '../../hooks/use-toast';
import { listRemoteServers } from '../../services/remoteServerService';
import { RemoteServer } from '../../types/remoteServer';
import { Button } from '../ui/button';
import CreateRemoteServerDialog from './CreateRemoteServerDialog';
import RemoteServersList from './RemoteServersList';

const IntegrationsSection: React.FC = () => {
  const [servers, setServers] = useState<RemoteServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { toast } = useToast();

  // Load remote servers
  const loadServers = async () => {
    try {
      setLoading(true);
      const data = await listRemoteServers();
      setServers(data);
    } catch (error: any) {
      console.error('Error loading remote servers:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load integrations',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServers();
  }, []);

  const handleServerCreated = () => {
    // Disabled - Coming Soon
    return;
  };

  const handleServerDeleted = () => {
    // Disabled - Coming Soon
    return;
  };

  const handleServerUpdated = () => {
    // Disabled - Coming Soon
    return;
  };

  const handleAddIntegration = () => {
    // Disabled - Coming Soon
    return;
  };

  return (
    <div className="space-y-6 relative">
      {/* Coming Soon Overlay */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
        <div className="text-center p-8 bg-card border-2 border-dashed rounded-lg max-w-md shadow-lg">
          <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-semibold mb-2">Coming Soon</h3>
          <p className="text-muted-foreground">
            Integrations functionality is currently under development and will be available soon.
          </p>
        </div>
      </div>

      {/* Original Content (visible but disabled) */}
      <div className="opacity-50 pointer-events-none">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Integrations</h2>
            <p className="text-muted-foreground mt-1">
              Connect to external databases and services
            </p>
          </div>
          <Button onClick={handleAddIntegration} disabled>
            <Plus className="mr-2 h-4 w-4" />
            Add Integration
          </Button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Empty State */}
        {!loading && servers.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed rounded-lg">
            <Database className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No integrations yet</h3>
            <p className="text-muted-foreground text-center mb-4 max-w-md">
              Connect to external databases like PostgreSQL or services like Stripe to
              access remote data.
            </p>
            <Button onClick={handleAddIntegration} disabled>
              <Plus className="mr-2 h-4 w-4" />
              Add Your First Integration
            </Button>
          </div>
        )}

        {/* Servers List */}
        {!loading && servers.length > 0 && (
          <RemoteServersList
            servers={servers}
            onServerDeleted={handleServerDeleted}
            onServerUpdated={handleServerUpdated}
          />
        )}

        {/* Create Dialog - Prevent from opening */}
        {false && (
          <CreateRemoteServerDialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
            onServerCreated={handleServerCreated}
          />
        )}
      </div>
    </div>
  );
};

export default IntegrationsSection;
